import { adminDebtorsApi, type AdminDebtor } from '@/api/admin/adminDebtorsApi';
import { AdminPager } from '@/components/admin/AdminPager';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePageResetOnFilterChange } from '@/hooks/usePageResetOnFilterChange';
import { currencyFormatter } from '@/utils/movements-utils';
import { handleGlobalError } from '@/utils/error-handler';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 20;

const AdminDeudaPage = () => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [debtors, setDebtors] = useState<AdminDebtor[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const debouncedQuery = useDebouncedValue(query);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    // Two fetches can be in flight at once (a new term resets the page, and
    // both changes fire a load). Only the newest response may write to state.
    const current = ++requestId.current;
    setIsLoading(true);
    try {
      const result = await adminDebtorsApi.list({
        query: debouncedQuery,
        page,
        size: PAGE_SIZE,
      });
      if (current !== requestId.current) return;
      setDebtors(result.content);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (current !== requestId.current) return;
      handleGlobalError(
        error,
        'No se pudo cargar la deuda',
        'Revisá la conexión e intentá de nuevo.',
      );
    } finally {
      if (current === requestId.current) {
        setIsLoading(false);
      }
    }
  }, [debouncedQuery, page]);

  useEffect(() => {
    load();
  }, [load]);

  usePageResetOnFilterChange(debouncedQuery, page, setPage);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Deuda</h1>
        <p className="text-sm text-gray-500">
          Quién le debe a GetMatch, de mayor a menor. Tocá una fila para abrir la
          ficha con sus movimientos.
        </p>
      </div>

      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Buscar por nombre o email"
        className="max-w-md"
      />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-12" />
          ))}
        </div>
      ) : debtors.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {debouncedQuery.trim()
            ? 'No hay deudores que coincidan.'
            : 'No hay deuda pendiente.'}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Persona</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Deuda</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {debtors.map((debtor) => (
              <TableRow key={debtor.pk}>
                <TableCell className="font-medium">
                  <Link
                    to={`/admin/usuarios/${debtor.pk}`}
                    className="text-blue-600 hover:underline"
                  >
                    {debtor.name || debtor.email}
                  </Link>
                </TableCell>
                <TableCell className="text-gray-600">{debtor.email}</TableCell>
                <TableCell className="text-right font-medium">
                  {currencyFormatter.format(debtor.debt)}
                </TableCell>
                <TableCell>
                  {debtor.hasPendingRequest ? (
                    // Without this the admin chases someone who already paid and
                    // is waiting on a review that the admin themselves owes them.
                    <Badge
                      variant="outline"
                      className="border-amber-200 bg-amber-100 text-amber-700"
                    >
                      Transferencia en revisión
                    </Badge>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AdminPager
        page={page}
        totalPages={totalPages}
        isLoading={isLoading}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminDeudaPage;
